/**
 * @file 顶部 Toast（全局错误/成功提示）
 * 为什么自己写：三端视觉必须一致（PRD 12 章），引入 UI 库会把品牌紫与圆角风格拆散；
 * 这里只做「栈式、可自动消失、带错误码与 trace_id」的最小实现。
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { describeError, shouldToast } from './errors';
import { useRuntime } from '../auth/context';

/** Toast 语义类型。 */
export type ToastKind = 'error' | 'success' | 'info';

/** 一条 Toast。 */
export interface ToastItem {
  /** 自增 id，用作 React key 与关闭句柄。 */
  id: number;
  /** 语义类型，决定左侧色条。 */
  kind: ToastKind;
  /** 标题（一句话说清发生了什么）。 */
  title: string;
  /** 补充说明（多为错误文案）。 */
  message?: string;
  /** 错误码，展示在底部等宽字体里，便于报障。 */
  code?: string;
  /** 追踪号。 */
  traceId?: string;
}

/** Toast 能力集合。 */
export interface ToastApi {
  /** 推送一条自定义 Toast。 */
  push(item: Omit<ToastItem, 'id'>): void;
  /** 推送错误 Toast（自动按 code 映射文案）。 */
  error(title: string, error?: unknown): void;
  /** 推送成功 Toast。 */
  success(title: string, message?: string): void;
  /** 关闭指定 Toast。 */
  dismiss(id: number): void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** 自动消失时间（毫秒）：错误多留一会儿，方便用户抄错误码。 */
const AUTO_DISMISS_MS = 6000;

/**
 * @what 提供 Toast 能力并渲染视口
 * @param props.children 子树
 * @return JSX
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const timers = useRef(new Map<number, number>());

  /**
   * @what 关闭并清理定时器
   * @param id Toast id
   * @return void 无返回值
   */
  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  /**
   * @what 入栈一条 Toast，最多保留 4 条
   * @param item 不含 id 的 Toast
   * @return void 无返回值
   */
  const push = useCallback(
    (item: Omit<ToastItem, 'id'>) => {
      seq.current += 1;
      const id = seq.current;
      setItems((prev) => [...prev.slice(-3), { ...item, id }]);
      const timer = window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
      timers.current.set(id, timer);
    },
    [dismiss],
  );

  /**
   * @what 错误 Toast：按 ApiError.code 映射文案
   * @param title 标题
   * @param error 任意异常
   * @return void 无返回值
   */
  const error = useCallback(
    (title: string, cause?: unknown) => {
      const described = describeError(cause);
      push({
        kind: 'error',
        title,
        message: described.message,
        ...(described.code ? { code: described.code } : {}),
        ...(described.traceId ? { traceId: described.traceId } : {}),
      });
    },
    [push],
  );

  /**
   * @what 成功 Toast
   * @param title 标题
   * @param message 补充说明
   * @return void 无返回值
   */
  const success = useCallback(
    (title: string, message?: string) => {
      push({ kind: 'success', title, ...(message ? { message } : {}) });
    },
    [push],
  );

  const api = useMemo<ToastApi>(() => ({ push, error, success, dismiss }), [push, error, success, dismiss]);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) window.clearTimeout(timer);
      pending.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="lx-toasts" role="status" aria-live="polite">
        {items.map((item) => (
          <div key={item.id} className={`lx-toast lx-toast--${item.kind}`}>
            <div className="lx-toast__body">
              <div className="lx-toast__title">{item.title}</div>
              {item.message ? <div className="lx-toast__text">{item.message}</div> : null}
              {item.code || item.traceId ? (
                <div className="lx-toast__code">
                  {item.code ? <span>code: {item.code}</span> : null}
                  {item.code && item.traceId ? ' · ' : null}
                  {item.traceId ? <span>trace: {item.traceId}</span> : null}
                </div>
              ) : null}
            </div>
            <button type="button" className="lx-toast__close" aria-label="关闭提示" onClick={() => dismiss(item.id)}>
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * @what 取 Toast 能力
 * @return ToastApi
 * @throws {Error} 未包在 ToastProvider 内时抛出
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast 必须在 ToastProvider 内使用');
  return api;
}

/**
 * @what 把 api-client 的 onError 接到全局 Toast
 * @return null（无 UI）
 */
export function GlobalErrorBridge(): null {
  const runtime = useRuntime();
  const toast = useToast();

  useEffect(() => {
    runtime.setErrorHandler((error) => {
      if (shouldToast(error)) toast.error('接口返回错误', error);
    });
    return () => runtime.setErrorHandler(null);
  }, [runtime, toast]);

  return null;
}
